import AppLayout from "@/components/AppLayout"
import LoginLoader from "@/components/LoginLoader"
import PptInput from "@/components/PptInput"
import {useState} from "react"
import TwilioLinkedNumbers from "@/components/apps/twilio/TwilioLinkedNumbers"
import HouseIcon from "@material-ui/icons/House"
import BusinessIcon from "@material-ui/icons/Business"
import SearchIcon from "@material-ui/icons/Search"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import TwilioAvailableNumbers from "@/components/apps/twilio/TwilioAvailableNumbers"
import TwilioConnect from "@/components/apps/twilio/TwilioConnect"
import {trpc} from "@/lib/requests/index"

export default function TwilioSettings(): JSX.Element {
  const twilioStatusQuery = trpc.useQuery([
    "forwardingPhoneNumber.twilioConnectionStatus"
  ])
  const twilioAccountConnected = (
    twilioStatusQuery.data?.twilioAccountConnected || false
  )
  const userToken = trpc.useQuery(["user.token"])
  const token = userToken.data || "none"

  const [showTwilioConnectPanel, setShowTwilioConnectPanel] = useState(false)
  type SearchDisplayNumber = "none" | "local" | "tollFree"
  const [numberSearchDisplayStatus, setNumberSearchDisplayStatus] = useState<
    SearchDisplayNumber
  >("none") // none, local, tollFree
  const [localAreaCode, setLocalAreaCode] = useState("")

  const customPhoneNumbersQuery = trpc.useQuery([
    "leadMagnet.customPhoneNumbers"
  ])

  const trpcContext = trpc.useContext()

  const searchLocalNumbersQuery = trpc.useQuery([
    "forwardingPhoneNumber.searchLocalNumbers", 
    {areaCode: localAreaCode}
  ], {enabled: false})

  const localNumbersLoading = searchLocalNumbersQuery.isLoading
  const localNumbersData = searchLocalNumbersQuery.data

  const handleLocalPhoneSearchClick = () => {
    searchLocalNumbersQuery.refetch()
  }

  const searchTollFreeNumbersQuery = trpc.useQuery([
    "forwardingPhoneNumber.searchTollFreeNumbers"
  ], {enabled: false})

  const tollFreeNumbersLoading = searchTollFreeNumbersQuery.isLoading
  const tollFreeNumbersData = searchTollFreeNumbersQuery.data
  const handleTollFreeSearchClick = () => {
    searchTollFreeNumbersQuery.refetch()
  }

  const handleTwilioNewConnect = async () => {
    try {
      if (twilioAccountConnected) {
        // update our cache if it changed
        // otherwise the twilio SID keys are stored on the server already
        trpcContext.setQueryData(["forwardingPhoneNumber.twilioConnectionStatus"], (old) => {
          if (!old) {
            return {twilioAccountConnected: false}
          }
          return {...old, twilioAccountConnected: true}
        })
      }
      setShowTwilioConnectPanel(false)
    } catch (error) {
      console.log(error)
    }
  }

  const purchasePhoneNumberMut = trpc.useMutation(["leadMagnet.purchaseCustomPhoneNumber"])
  async function handleNumberPurchase(phoneNumber: string) {
    console.log("purchase: " + phoneNumber)
    setNumberSearchDisplayStatus("none")
    const res = await purchasePhoneNumberMut.mutateAsync({phoneNumber})
    if (!res) {
      window.alert("An error occurred when purchasing phone number!")
      return
    }
    trpcContext.setQueryData(["leadMagnet.customPhoneNumbers"], (old) => {
      if (!old) {
        return []
      }
      return [res, ...old]
    })
  }

  const releasePhoneNumberMut = trpc.useMutation(["leadMagnet.releaseCustomPhoneNumber"])
  const handleNumberRelease = (phoneNumber: string) => {
    console.log("release: " + phoneNumber)
    trpcContext.setQueryData(["leadMagnet.customPhoneNumbers"], (old) => {
      if (!old) {
        return []
      }
      const copy = [...old]
      const targetIndex = copy.findIndex(
        (p) => p.phoneNumber === phoneNumber
      )
      if (targetIndex < 0) {
        return copy
      }
      copy.splice(targetIndex, 1)
      return copy
    })
    releasePhoneNumberMut.mutate({phoneNumber})
  }

  const linkedPhoneNumbers = customPhoneNumbersQuery.data || []

  // todo: add in 'unlink' function ?
  const loading = twilioStatusQuery.isLoading || userToken.isLoading
  const error = twilioStatusQuery.isError

  return <AppLayout requiredAuth="user">
    
    {loading ? (
      <div className="w-full h-full flex items-center flex-col justify-center">
        <LoginLoader/>
      </div>
    ) : null}
    {error ? (
      <pre>
        {"Error: "}
        {twilioStatusQuery.error}
      </pre>
    ) : null}
    {!loading && !error ? (
      <div className="flex flex-col items-center justify-start w-full relative overflow-y-auto">
        <div className="flex flex-col items-center my-8 w-1/2">
          <h1 className="text-3xl sm:text-4xl font-display text-center">
            {"Twilio Settings"}
          </h1>
          <h3 className="text-gray-600 opacity-75 text-center">
            {"Use custom SMS and Voice Lead Magnets"}
          </h3>
        </div>

        {!twilioAccountConnected || showTwilioConnectPanel ? (
          <TwilioConnect
            token={token}
            onConnect={handleTwilioNewConnect}
          />
        ) : (
          <div className="box-content p-4 flex flex-row items-center">
            <span className="text-ppt-success">
              {"Your Twilio account is already linked."}
            </span>
            <button
              onClick={() => setShowTwilioConnectPanel(true)}
              className="text-ppt-lighttext border-b-2 border-ppt-lighttext hover:text-ppt-blue hover:border-ppt-blue font-display text-2lg ml-8"
            >
              {"Connect Again"}
            </button>
          </div>
        )}
        {twilioAccountConnected ? (
          <>
            <TwilioLinkedNumbers
              linkedNumbers={linkedPhoneNumbers}
              onRemove={handleNumberRelease}
            />
            {numberSearchDisplayStatus === "none" ? (
              <div className="w-3/4 flex items-center justify-around mt-8">
                <button
                  onClick={() => setNumberSearchDisplayStatus("local")}
                  className="flex flex-col items-center box-content py-4 w-40 text-ppt-success hover-scale"
                >
                  <div className="flex items-center">
                    <SearchIcon
                      style={{ height: "3rem", width: "3rem" }}
                    ></SearchIcon>
                    <HouseIcon
                      style={{ height: "3rem", width: "3rem" }}
                    ></HouseIcon>
                  </div>
                  <span className="col-span-2">{"Search Local Numbers"}</span>
                </button>
                <button
                  onClick={() => {
                    handleTollFreeSearchClick()
                    setNumberSearchDisplayStatus("tollFree")
                  }}
                  className="flex flex-col items-center box-content py-4 w-40 text-ppt-blue hover-scale"
                >
                  <div className="flex items-center">
                    <SearchIcon
                      style={{ height: "3rem", width: "3rem" }}
                    ></SearchIcon>
                    <BusinessIcon
                      style={{ height: "3rem", width: "3rem" }}
                    ></BusinessIcon>
                  </div>
                  <span className="col-span-2">
                    {"Search Toll Free Numbers"}
                  </span>
                </button>
              </div>
            ) : null}
            {numberSearchDisplayStatus === "local" ? (
              <div className="flex flex-col w-3/4 mt-4 box-content p-4">
                <div className="flex flex-row items-center justify-between w-full">
                  <button
                    onClick={() => setNumberSearchDisplayStatus("none")}
                    className="font-display font-2xl pl-4 flex items-center mb-2"
                  >
                    <ArrowBackIcon></ArrowBackIcon>
                    Go Back
                  </button>
                  <div className="flex items-center">
                    <PptInput
                      className="w-24 text-black"
                      labelText="Area Code"
                      name="areacode"
                      type="text"
                      value={localAreaCode}
                      onChange={(e) => setLocalAreaCode(e.target.value)}
                    />
                    <button
                      onClick={handleLocalPhoneSearchClick}
                      className="text-ppt-blue text-2xl font-display border-b-2 border-ppt-blue hover:border-ppt-success hover:text-ppt-success ml-2"
                    >
                      <SearchIcon></SearchIcon>
                      search
                    </button>
                  </div>
                </div>
                {localNumbersLoading ? (
                  <span>{"loading local numbers..."}</span>
                ) : null}
                {localNumbersData ? (
                  <TwilioAvailableNumbers
                    numbers={localNumbersData}
                    onPurchase={handleNumberPurchase}
                    showLocationData
                  />
                ) : null}
              </div>
            ) : null}
            {numberSearchDisplayStatus === "tollFree" ? (
              <div className="flex flex-col w-3/4 mt-4 box-content p-4">
                <button
                  onClick={() => setNumberSearchDisplayStatus("none")}
                  className="font-display font-2xl pl-4 flex items-center mb-2"
                >
                  <ArrowBackIcon/>
                  Go Back
                </button>
                {tollFreeNumbersLoading ? (
                  <span>{"Loading toll free numbers..."}</span>
                ) : null}
                {tollFreeNumbersData ? (
                  <TwilioAvailableNumbers
                    numbers={tollFreeNumbersData}
                    onPurchase={handleNumberPurchase}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    ) : null}
  </AppLayout>
}